import React from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import AuthCard from "../../components/layout/AuthCard";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import ReqMark from "./ReqMark";
import { useLazyCheckEmailExistsQuery } from "../../redux/api/authApi";

// Define the validation schema
const schema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required("Please enter email")
    .email("Invalid email")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email"),
  password: yup
    .string()
    .trim()
    .required("Please enter password")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_+\-~()`{}[\]:;"'<>/?.,|\\]).{8,}$/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    ),
  confirmPassword: yup
    .string()
    .trim()
    .required("Please confirm password")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
  // ownerType: yup.string().when("role", {
  //   is: "assetOwner", // If role is 'assetOwner', ownerType becomes required
  //   then: yup.string().required("Please select entity type"),
  // }),
  // investorType: yup.string().when("role", {
  //   is: "investor", // If role is 'investor', investorType becomes required
  //   then: yup.string().required("Please select entity type"),
  // }),
});

const ENTITY_CYAN = "#00D1FF";
const ENTITY_CYAN_SOFT = "rgba(0, 209, 255, 0.45)";

function EntityTypeCard({ selected, onClick, icon: Icon, label }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 112,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 1,
        py: 1.5,
        borderRadius: "14px",
        cursor: "pointer",
        border: "2px solid",
        borderColor: selected ? ENTITY_CYAN : "rgba(148, 163, 184, 0.28)",
        bgcolor: selected ? "rgba(0, 209, 255, 0.08)" : "rgba(15, 23, 42, 0.55)",
        boxShadow: selected
          ? `0 0 20px ${ENTITY_CYAN_SOFT}, inset 0 0 20px rgba(0, 209, 255, 0.06)`
          : "none",
        transition:
          "border-color 0.2s, box-shadow 0.2s, background-color 0.2s, color 0.2s",
        "&:hover": {
          borderColor: selected ? ENTITY_CYAN : "rgba(148, 163, 184, 0.45)",
          bgcolor: selected ? "rgba(0, 209, 255, 0.1)" : "rgba(30, 41, 59, 0.65)",
        },
        "&:focus-visible": {
          outline: `2px solid ${ENTITY_CYAN}`,
          outlineOffset: 2,
        },
      }}
    >
      <Icon
        sx={{
          fontSize: 36,
          color: selected ? ENTITY_CYAN : "#64748b",
          transition: "color 0.2s",
        }}
      />
      <Typography
        fontWeight={700}
        fontSize={{ xs: 12, sm: 13 }}
        sx={{
          color: selected ? ENTITY_CYAN : "#e2e8f0",
          textAlign: "center",
          lineHeight: 1.25,
          transition: "color 0.2s",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function Step1Account({ nextStep, defaultValues = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [checkingEmail, setCheckingEmail] = React.useState(false);

  const [checkEmailExists] = useLazyCheckEmailExistsQuery();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      email: defaultValues.email || "",
      password: "",
      confirmPassword: "",
      ownerType: defaultValues.ownerType || "individual",
      investorType: defaultValues.investorType || "individual",
    },
  });

  const ownerType = watch("ownerType");
  const investorType = watch("investorType");

  const onSubmit = async (data) => {
    const payload = { ...data };

    if (role === "assetOwner") {
      delete payload.investorType;
    } else if (role === "investor") {
      delete payload.ownerType;
    }

    setCheckingEmail(true);
    try {
      const result = await checkEmailExists({ email: data.email, role }, true);
      if (result?.error) {
        setError("email", {
          type: "manual",
          message:
            result.error?.data?.message ||
            "Unable to verify email. Please try again.",
        });
        return;
      }
      console.warn("Email Check Result -> ", result);
      const exists = !result?.data?.data?.success;
      if (exists) {
        setError("email", {
          type: "manual",
          message: "This email is already registered. Please use a different email.",
        });
        return;
      }
    } catch {
      setError("email", {
        type: "manual",
        message: "Unable to verify email. Please try again.",
      });
      return;
    } finally {
      setCheckingEmail(false);
    }

    if (typeof nextStep === "function") {
      nextStep(payload);
    }
  };

  const handleEntityTypeSelect = (type, r) => {
    if (r === "assetOwner") {
      setValue("ownerType", type, { shouldDirty: true, shouldValidate: true });
    }
    if (r === "investor") {
      setValue("investorType", type, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 3,
        boxSizing: "border-box",
        bgcolor: "#030712",
      }}
    >
    <AuthCard>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={700}>
            Create Account
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: "#38bdf8",
              borderColor: "rgba(56,189,248,0.4)",
              borderRadius: "12px",
              textTransform: "none",
              "&:hover": { background: "rgba(56,189,248,0.1)" },
            }}
          >
            {role === "investor"
              ? "Investor"
              : role === "assetOwner"
              ? "Asset Owner"
              : "Admin"}
          </Button>
        </Stack>

        <Typography color="text.secondary" sx={{ mb: 0.5 }}>
          Step 1 of 5 - Account Details
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
          * All fields in this step are required.
        </Typography>

        {/* Segmented Progress Bar */}
        <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
          {[1, 2, 3, 4, 5].map((bar, index) => (
            <Box
              key={bar}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: 1,
                backgroundColor:
                  index < 1 ? "#0ea5e9" : "rgba(255,255,255,0.1)",
              }}
            />
          ))}
        </Stack>

        <Stack spacing={2}>
          <Box>
            {/* <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Entity Type
              <ReqMark />
            </Typography> */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 2, minWidth: 0 }}>
              {role === "assetOwner" && (
                <>
                  {/* <EntityTypeCard
                    selected={ownerType === "individual"}
                    onClick={() => handleEntityTypeSelect("individual", role)}
                    icon={PersonOutlineIcon}
                    label="Individual Owner"
                  /> */}
                  {/* <EntityTypeCard
                    selected={ownerType === "corporate"}
                    onClick={() => handleEntityTypeSelect("corporate", role)}
                    icon={BusinessIcon}
                    label="Corporate"
                  /> */}
                </>
              )}
              {role === "investor" && (
                <>
                  {/* <EntityTypeCard
                    selected={investorType === "individual"}
                    onClick={() => handleEntityTypeSelect("individual", role)}
                    icon={PersonOutlineIcon}
                    label="Individual"
                  /> */}
                  {/* <EntityTypeCard
                    selected={investorType === "institution"}
                    onClick={() => handleEntityTypeSelect("institution", role)}
                    icon={BusinessIcon}
                    label="Institution"
                  /> */}
                </>
              )}
            </Stack>
            {errors.ownerType && role === "assetOwner" && (
              <Typography variant="caption" color="error">
                {errors.ownerType?.message}
              </Typography>
            )}
            {errors.investorType && role === "investor" && (
              <Typography variant="caption" color="error">
                {errors.investorType?.message}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Email Address
              <ReqMark />
            </Typography>
            <TextField
              placeholder="you@example.com"
              fullWidth
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              InputProps={{
                sx: {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "10px",
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Password
              <ReqMark />
            </Typography>
            <TextField
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              fullWidth
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                sx: {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "10px",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((p) => !p)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ color: "#94a3b8" }} />
                      ) : (
                        <Visibility sx={{ color: "#94a3b8" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Confirm Password
              <ReqMark />
            </Typography>
            <TextField
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              fullWidth
              {...register("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                sx: {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "10px",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff sx={{ color: "#94a3b8" }} />
                      ) : (
                        <Visibility sx={{ color: "#94a3b8" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={checkingEmail}
          endIcon={<ArrowForwardIcon />}
          sx={{
            mt: 3,
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          {checkingEmail ? "Checking..." : "Continue"}
        </Button>

        <Typography textAlign="center" sx={{ mt: 2 }}>
          Already have an account?{" "}
          <Button
            onClick={() => navigate(`/signin?role=${role}`)}
            sx={{
              color: "#38bdf8",
              textTransform: "none",
            }}
          >
            Sign In
          </Button>
        </Typography>
      </Box>
    </AuthCard>
    </Box>
  );
}
